import React, { useState, useEffect, useRef, FC } from 'react';
import {
  Box,
  Text,
  Image,
  Flex,
  HStack,
  IconButton,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  useDisclosure,
  TableContainer,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  List,
  ListItem,
} from '@chakra-ui/react';
import {
  InfoOutlineIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@chakra-ui/icons';
import { motion } from 'framer-motion';
import {
  FaThumbsUp,
  FaThumbsDown,
  FaComment,
  FaDownload,
  FaCode,
} from 'react-icons/fa';
import DOMPurify from 'dompurify';
import OrcanaLogo from '../../assets/images/orcana_logo.png';
import trend from '../../assets/demoFiles/trend.png';
import pie from '../../assets/demoFiles/pie.png';
import { Line, Bar, Pie as PieChart } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title as ChartTitle,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
} from 'chart.js';
import ZoomPlugin from 'chartjs-plugin-zoom';
import Tree from 'react-d3-tree';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  ChartTitle,
  ChartTooltip,
  ChartLegend,
  ZoomPlugin
);

const MotionFlex = motion(Flex);

export interface TreeNode {
  name: string;
  attributes?: { [key: string]: any };
  children?: TreeNode[];
}

export interface VisualMetadata {
  data?: number[];
  default_type: 'line' | 'bar' | 'pie' | 'tree' | 'table';
  labels?: string[];
  title: string;
  types?: string[];
  x_axis_label?: string;
  y_axis_label?: string;
  datasets?: {
    label: string;
    data: (number | string)[];
    borderColor?: string;
    backgroundColor?: string;
    fill?: boolean | number;
    tension?: number;
  }[];
  backgroundColor?: string[];
  borderColor?: string[];
  treeData?: TreeNode[];
  tableHeaders?: string[];
  tableRows?: (string | number)[][];
}

export type Message = {
  message_id: number;
  inquiry_id: number;
  user_id: number;
  created_at: string;
  message_source: 'user' | 'orcana';
  body_original: string;
  body_rephrased: string | null;
  intent: string;
  analytics_run_id: number | null;
  feedback_type?: 'thumbs-up' | 'thumbs-down' | '';
  comments?: string | null;
  image?: string;
  message_metadata?: {
    image?: string;
    visualMetadata?: VisualMetadata | VisualMetadata[];
    hoverInfo?: { [key: string]: any };
    user_plan?: string;
    code_plan?: string;
    code?: string;
  };
};

export type MessageTemplateProps = {
  message: Message;
  isHovered: boolean;
  showHoverPanel: boolean;
  thumbsUp: boolean;
  thumbsDown: boolean;
  comments: string;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onThumbsUp: () => void;
  onThumbsDown: () => void;
  onOpenComments: () => void;
};

const TreeComponent: FC<{ treeData: TreeNode[] }> = ({ treeData }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dims, setDims] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const el = containerRef.current;
    if (el) {
      const { width, height } = el.getBoundingClientRect();
      setDims({ width, height });
    }
  }, []);

  const centerX = dims.width / 2 || 300;

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: '600px',
        overflow: 'auto',
      }}
    >
      <Tree
        data={treeData}
        orientation="vertical"
        translate={{ x: centerX, y: 50 }}
        pathFunc="step"
        zoomable
        nodeSize={{ x: 200, y: 150 }}
        separation={{ siblings: 1.5, nonSiblings: 2 }}
        scaleExtent={{ min: 0.5, max: 2 }}
      />
    </div>
  );
};

const ChartComponent: FC<{
  visualMetadata: VisualMetadata;
  chartRef: React.Ref<any>;
}> = ({ visualMetadata, chartRef }) => {
  const parseValue = (v: number | string) =>
    typeof v === 'string' ? parseFloat(v.replace('%', '')) : v;

  if (visualMetadata.default_type === 'tree' && visualMetadata.treeData) {
    return <TreeComponent treeData={visualMetadata.treeData} />;
  }

  const fallbackData = (visualMetadata.data || []).map(parseValue);

  const data = {
    labels: visualMetadata.labels,
    datasets:
      visualMetadata.datasets && visualMetadata.datasets.length > 0
        ? visualMetadata.datasets.map((ds) => ({
            ...ds,
            data: ds.data.map(parseValue),
          }))
        : [
            {
              label: visualMetadata.y_axis_label,
              data: fallbackData,
              backgroundColor:
                visualMetadata.backgroundColor || 'rgba(75,192,192,0.2)',
              borderColor:
                visualMetadata.borderColor || 'rgba(75,192,192,1)',
              fill: true,
              tension: 0.4,
            },
          ],
  };

  const options: any = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      title: { display: true, text: visualMetadata.title },
      legend: { display: true },
      zoom: {
        pan: { enabled: true, mode: 'x' },
        zoom: {
          wheel: { enabled: true },
          pinch: { enabled: true },
          mode: 'x',
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: visualMetadata.x_axis_label },
        ticks: {
          autoSkip: false,
          maxRotation: 90,
          minRotation: 45,
          callback: function (_value: any, index: number) {
            const rawLabel = (this as any).chart.data.labels[index] as string;
            return rawLabel.length > 10
              ? rawLabel.match(/.{1,10}/g)
              : rawLabel;
          },
        },
      },
      y: {
        title: { display: true, text: visualMetadata.y_axis_label },
      },
    },
  };

  switch (visualMetadata.default_type) {
    case 'bar':
      return <Bar ref={chartRef} data={data} options={options} />;
    case 'pie':
      return <PieChart ref={chartRef} data={data} options={options} />;
    default:
      return <Line ref={chartRef} data={data} options={options} />;
  }
};

const TableComponent: FC<{ headers: string[]; rows: (string | number)[][] }> = ({
  headers,
  rows,
}) => (
  <TableContainer
    border="1px solid"
    borderColor="gray.200"
    borderRadius="md"
    overflowY="auto"
    maxH="400px"
  >
    <Table variant="simple" size="sm">
      <Thead bg="gray.100">
        <Tr>{headers.map((h) => <Th key={h}>{h}</Th>)}</Tr>
      </Thead>
      <Tbody>
        {rows.map((row, i) => (
          <Tr key={i} _even={{ bg: 'gray.50' }}>
            {row.map((cell, j) => <Td key={j}>{cell}</Td>)}
          </Tr>
        ))}
      </Tbody>
    </Table>
  </TableContainer>
);

const MessageTemplate: FC<MessageTemplateProps> = ({
  message,
  showHoverPanel,
  thumbsUp,
  thumbsDown,
  comments,
  onMouseEnter,
  onMouseLeave,
  onThumbsUp,
  onThumbsDown,
  onOpenComments,
}) => {
  const isUser = message.message_source === 'user';
  const { isOpen, onOpen, onClose } = useDisclosure(); // image modal
  const {
    isOpen: isReasonOpen,
    onOpen: onReasonOpen,
    onClose: onReasonClose,
  } = useDisclosure(); // reasoning/code modal

  const [hoveringImage, setHoveringImage] = useState(false);
  const [imageValue, setImageValue] = useState<string>('');
  const [chartIndex, setChartIndex] = useState(0);
  const [showControls, setShowControls] = useState(false);
  const chartRef = useRef<any>(null);

  const visualList: VisualMetadata[] = Array.isArray(
    message.message_metadata?.visualMetadata
  )
    ? (message.message_metadata.visualMetadata as VisualMetadata[])
    : message.message_metadata?.visualMetadata
    ? [message.message_metadata.visualMetadata as VisualMetadata]
    : [];

  useEffect(() => {
    const newVal =
      (message.message_metadata && message.message_metadata.image) ||
      message.image ||
      '';
    setImageValue(newVal);
  }, [message]);

  const getImageSource = (imageName: string): string => {
    switch (imageName) {
      case 'trend':
        return trend;
      case 'pie':
        return pie;
      default:
        return imageName;
    }
  };

const downloadVisual = () => {
  const visual = visualList[chartIndex];
  
  if (visual.default_type === 'table'
      && visual.tableHeaders
      && visual.tableRows) {
    // 1. Build CSV content
    const { tableHeaders: headers, tableRows: rows, title } = visual;
    const csvLines = [
      headers.join(','),                  // header row
      ...rows.map(row =>
        row.map(cell => `"${cell}"`).join(',') // quote every cell
      )
    ];
    const csvContent = csvLines.join('\n');
    
    // 2. Create and click a temporary link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title || 'table'}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } else {
    // fallback: download chart image
    let chart: any = chartRef.current;
    if (chart?.chartInstance) chart = chart.chartInstance;
    const fn = chart?.toBase64Image ?? chart?.chart?.toBase64Image;
    if (!fn) return console.error('Chart instance not found');
    const url = fn.call(chart);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${visualList[chartIndex]?.title || 'chart'}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
};

  return (
    <>
      <MotionFlex
        initial={{ opacity: 0, x: isUser ? 30 : -30 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4 }}
        justifyContent={isUser ? 'flex-end' : 'flex-start'}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        mb="4"
      >
        <Flex
          width={isUser ? '100%' : '90%'}
          maxWidth={isUser ? '90%' : '100%'}
          alignItems="flex-end"
          justifyContent={isUser ? 'flex-end' : 'flex-start'}
          gap={3}
        >
          {!isUser && (
            <Image
              src={OrcanaLogo}
              alt="Orcana Logo"
              boxSize="40px"
              borderRadius="full"
            />
          )}

          <Box
            flex="1"
            bg={isUser ? 'primary.500' : 'gray.50'}
            color={isUser ? 'white' : 'black'}
            px={4}
            py={3}
            borderRadius="lg"
            boxShadow="md"
            position="relative"
            maxWidth="80%"
          >
            {visualList.length > 0 && (
              <Flex
                mt={2}
                align="center"
                justify="center"
                gap={2}
                onMouseEnter={() => setShowControls(true)}
                onMouseLeave={() => setShowControls(false)}
              >
                <IconButton
                  aria-label="Previous chart"
                  icon={<ChevronLeftIcon />}
                  size="sm"
                  opacity={showControls ? 1 : 0}
                  pointerEvents={showControls ? 'auto' : 'none'}
                  onClick={() =>
                    setChartIndex((i) =>
                      i === 0 ? visualList.length - 1 : i - 1
                    )
                  }
                />

                <Box flex="1" maxW="800px" width="100%" height="400px">
                  {visualList[chartIndex].default_type === 'table' &&
                  visualList[chartIndex].tableHeaders &&
                  visualList[chartIndex].tableRows ? (
                    <TableComponent
                      headers={visualList[chartIndex].tableHeaders}
                      rows={visualList[chartIndex].tableRows}
                    />
                  ) : (
                    <ChartComponent
                      visualMetadata={visualList[chartIndex]}
                      chartRef={chartRef}
                    />
                  )}
                </Box>

                <IconButton
                  aria-label="Next chart"
                  icon={<ChevronRightIcon />}
                  size="sm"
                  opacity={showControls ? 1 : 0}
                  pointerEvents={showControls ? 'auto' : 'none'}
                  onClick={() =>
                    setChartIndex((i) =>
                      i === visualList.length - 1 ? 0 : i + 1
                    )
                  }
                />

                <Tooltip label="Download chart">
                  <IconButton
                    aria-label="Download"
                    icon={<FaDownload />}
                    size="sm"
                    opacity={showControls ? 1 : 0}
                    pointerEvents={showControls ? 'auto' : 'none'}
                    onClick={downloadVisual}
                  />
                </Tooltip>
              </Flex>
            )}

            {message.body_original && (
              <Box
                fontSize="md"
                fontWeight="medium"
                pl={6}
                pt={4}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(message.body_original),
                }}
              />
            )}

            {imageValue && visualList.length === 0 && (
              <MotionFlex
                mt={2}
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
                cursor="pointer"
                onClick={onOpen}
                onMouseEnter={() => setHoveringImage(true)}
                onMouseLeave={() => setHoveringImage(false)}
              >
                <Image
                  src={getImageSource(imageValue)}
                  alt="Image"
                  borderRadius="md"
                  maxH="300px"
                  objectFit="cover"
                  boxShadow={hoveringImage ? 'lg' : 'md'}
                />
              </MotionFlex>
            )}

            <Text fontSize="2xs" mt={1} opacity={0.6}>
              {new Date(message.created_at).toLocaleTimeString()}
            </Text>

            {(thumbsUp || thumbsDown) && (
              <Box mt={2}>
                <HStack spacing={3} p={1} borderRadius="lg" display="inline-flex">
                  {thumbsUp && (
                    <Tooltip label="Thumbs Up" hasArrow>
                      <IconButton
                        aria-label="Thumbs Up"
                        icon={<FaThumbsUp />}
                        size="sm"
                        variant="ghost"
                        onClick={onThumbsUp}
                      />
                    </Tooltip>
                  )}
                  {thumbsDown && (
                    <Tooltip label="Thumbs Down" hasArrow>
                      <IconButton
                        aria-label="Thumbs Down"
                        icon={<FaThumbsDown />}
                        size="sm"
                        variant="ghost"
                        onClick={onThumbsDown}
                      />
                    </Tooltip>
                  )}
                </HStack>
              </Box>
            )}

            {comments && comments.trim() !== '' && (
              <Box
                mt={2}
                borderLeft="2px solid #ccc"
                pl={3}
                pt={1}
                cursor="pointer"
                onClick={onOpenComments}
                display="inline-block"
              >
                <Text fontSize="sm" fontWeight="semibold" mb={1}>
                  Comment:
                </Text>
                <Text fontSize="sm">{comments}</Text>
              </Box>
            )}

            {showHoverPanel && message.message_source === 'orcana' && (
              <MotionFlex
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.2 }}
                style={{
                  position: 'absolute',
                  bottom: '24px',
                  right: '16px',
                  background: 'white',
                  border: '1px solid #ccc',
                  borderRadius: '8px',
                  padding: '8px',
                  zIndex: 10,
                }}
              >
                {message.message_metadata?.hoverInfo && (
                  <Tooltip
                    label={
                      <Box>
                        {Object.entries(
                          message.message_metadata.hoverInfo
                        ).map(([key, value]) => (
                          <Text key={key} fontSize="xs" color="white" mb={1}>
                            <strong>{key}:</strong> {value}
                          </Text>
                        ))}
                      </Box>
                    }
                    bg="primary.500"
                    color="white"
                    fontSize="xs"
                    hasArrow
                    placement="top"
                    p={2}
                    borderRadius="md"
                  >
                    <IconButton
                      aria-label="Info"
                      icon={<InfoOutlineIcon />}
                      size="sm"
                      variant="ghost"
                    />
                  </Tooltip>
                )}
                <Tooltip label="Thumbs Up" hasArrow>
                  <IconButton
                    aria-label="Thumbs Up"
                    icon={<FaThumbsUp />}
                    size="sm"
                    variant="ghost"
                    onClick={onThumbsUp}
                  />
                </Tooltip>
                <Tooltip label="Thumbs Down" hasArrow>
                  <IconButton
                    aria-label="Thumbs Down"
                    icon={<FaThumbsDown />}
                    size="sm"
                    variant="ghost"
                    onClick={onThumbsDown}
                  />
                </Tooltip>
                <Tooltip label="Comment" hasArrow>
                  <IconButton
                    aria-label="Comment"
                    icon={<FaComment />}
                    size="sm"
                    variant="ghost"
                    onClick={onOpenComments}
                  />
                </Tooltip>
                <Tooltip label="Reasoning & Code" hasArrow>
                  <IconButton
                    aria-label="Reasoning & Code"
                    icon={<FaCode />}
                    size="sm"
                    variant="ghost"
                    onClick={onReasonOpen}
                  />
                </Tooltip>
              </MotionFlex>
            )}
          </Box>
        </Flex>
      </MotionFlex>

      {/* Image preview modal */}
      {visualList.length === 0 && (
        <Modal isOpen={isOpen} onClose={onClose} isCentered size="xl">
          <ModalOverlay
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
          <ModalContent
            as={motion.div}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1.5, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            borderRadius="lg"
            overflow="hidden"
          >
            <ModalCloseButton />
            <ModalBody p={0}>
              <Image
                src={getImageSource(imageValue)}
                alt="Expanded visual"
                width="100%"
                maxHeight="80vh"
                objectFit="contain"
              />
            </ModalBody>
          </ModalContent>
        </Modal>
      )}

<Modal isOpen={isReasonOpen} onClose={onReasonClose} isCentered size="xl">
  <ModalOverlay />
  <ModalContent>
    <ModalCloseButton />
    <ModalBody>
      <Tabs variant="enclosed">
        <TabList>
          <Tab>User Plan</Tab>
          <Tab>Code Plan</Tab>
          <Tab>Code</Tab>
        </TabList>
        <TabPanels>
          {/* User Plan as a bulleted list */}
          <TabPanel>
            {Array.isArray(message.message_metadata?.user_plan) ? (
              <List spacing={2}>
                {message.message_metadata.user_plan!.map((step, idx) => (
                  <ListItem key={idx}>{step}</ListItem>
                ))}
              </List>
            ) : (
              <Box whiteSpace="pre-wrap">
                {message.message_metadata?.user_plan ||
                  'No user plan available.'}
              </Box>
            )}
          </TabPanel>

          {/* Code Plan as a bulleted list */}
          <TabPanel>
            {Array.isArray(message.message_metadata?.code_plan) ? (
              <List spacing={2}>
                {message.message_metadata.code_plan!.map((step, idx) => (
                  <ListItem key={idx}>{step}</ListItem>
                ))}
              </List>
            ) : (
              <Box whiteSpace="pre-wrap">
                {message.message_metadata?.code_plan ||
                  'No code plan available.'}
              </Box>
            )}
          </TabPanel>

          {/* Raw code block */}
          <TabPanel>
            <Box as="pre" whiteSpace="pre-wrap">
              {message.message_metadata?.code || 'No code available.'}
            </Box>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </ModalBody>
  </ModalContent>
</Modal>
    </>
  );
};

export default MessageTemplate;
